/**
 * Shared helpers for report row/card.
 */
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  REPORT_IN_PERSON_TAB_TO_TYPES as TAB_TO_TYPES,
  REPORT_PARTICIPANT_ROLE_ORDER,
  REPORT_STATUS_CONFIG,
  type PerTypeReportPageActions,
  type ReportInPersonTab as Tab,
  type ReportStatusConfig,
  SESSION_TYPE_FILTER_KEYS,
} from '@/components/reports/report-configs';
import { getSession } from '@/services/inperson/sessions.service';
import type { ReportFilters, SessionReport } from '@/types/inperson/report.types';
import {
  buildReportValidationOptions,
  getReportDisplayRating,
  normalizeAuthorFeedbacks,
  shouldShowBulkReportValidationModal,
  shouldShowReportValidationModal,
  type ReportValidationOptions,
} from '@/utils/report-author-feedback-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRowPermission } from '@/hooks/usePageActions';

export { resolveReportPermissions, toReportInPersonTab } from '@/utils/common-utils';

/** RBAC actions plus optional row delete. */
export type ReportPageActions = PerTypeReportPageActions & { canDeleteRow?: boolean };

/** Flattened status / author / date / rating for a report row. */
export interface DerivedReportDisplay {
  statusConfig: ReportStatusConfig;
  StatusIcon: ReportStatusConfig['icon'];
  authorName: string;
  entryDateText: string;
  displayRating: number | null;
  establishmentName: string | null;
}

/** Edit / validate disabled flags + tooltips. */
export interface ReportActionState {
  editDisabled: boolean;
  editTooltip: string;
  validateDisabled: boolean;
  validateTooltip: string;
}

/** Single-report validate: incomplete modal vs confirm. */
export type SingleValidateTarget =
  | {
      kind: 'incomplete';
      sessionId: string;
      reportId: string;
      validateOptions: ReportValidationOptions;
    }
  | { kind: 'confirm'; sessionId: string; reportId: string };

/** Bulk validate: incomplete options map vs confirm. */
export type BulkValidateTarget =
  | { kind: 'incomplete'; optionsByReportId: Record<string, ReportValidationOptions> }
  | { kind: 'confirm' };

/** Status badge, author, entry date, rating, and establishment. */
export const deriveReportDisplay = (report: SessionReport): DerivedReportDisplay => {
  const statusConfig = REPORT_STATUS_CONFIG[report.status] || REPORT_STATUS_CONFIG.draft;
  const authorName =
    report.authorName ||
    report.author?.displayName ||
    [report.author?.firstName, report.author?.lastName].filter(Boolean).join(' ') ||
    'Auteur inconnu';
  const entryDateText = report.filledAt
    ? format(new Date(report.filledAt), 'd MMM yyyy', { locale: fr })
    : '—';

  return {
    statusConfig,
    StatusIcon: statusConfig.icon,
    authorName,
    entryDateText,
    displayRating: getReportDisplayRating(report),
    establishmentName: report.session?.establishment?.name ?? null,
  };
};

/** Session date as `d MMM yyyy`, or `sessionDisplayName`. */
export const formatReportSessionDateLabel = (report: SessionReport): string =>
  report.session?.sessionDate
    ? format(new Date(report.session.sessionDate), 'd MMM yyyy', { locale: fr })
    : report.sessionDisplayName || 'Date inconnue';

/** Card title: display name, then formatted session date. */
export const formatReportSessionCardLabel = (report: SessionReport): string =>
  report.sessionDisplayName ||
  (report.session?.sessionDate
    ? format(new Date(report.session.sessionDate), 'd MMM yyyy', { locale: fr })
    : 'Date inconnue');

/** Validated locks edit; validate only when status is `submitted`. */
export const getReportActionState = (report: SessionReport): ReportActionState => {
  const editDisabled = report.status === 'validated';
  const validateDisabled = report.status !== 'submitted';
  return {
    editDisabled,
    editTooltip: editDisabled
      ? getErrorMessage('validatedReportLocked')
      : 'Modifier le compte rendu',
    validateDisabled,
    validateTooltip: validateDisabled
      ? getErrorMessage('reportMustBeSubmittedToValidate')
      : 'Valider le compte rendu',
  };
};

/** Local `YYYY-MM-DD` for {@link DatePicker} filter values. */
export function formatReportFilterDateToString(d?: Date): string | undefined {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parse a picker YYYY-MM-DD to local midnight or 23:59:59. */
export function parseReportFilterDateInput(val: string, endOfDay: boolean): Date {
  const [year, month, day] = val.split('-').map(Number);
  return endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59)
    : new Date(year, month - 1, day, 0, 0, 0);
}

/** Session-type filter keys for the current tab (`[]` when the tab has a single type). */
export function getReportSessionTypeFilterKeys(activeTab?: Tab | 'all'): readonly string[] {
  if (activeTab && activeTab !== 'all' && activeTab in TAB_TO_TYPES) {
    const tabTypes = TAB_TO_TYPES[activeTab];
    if (tabTypes.length <= 1) return [];
    return tabTypes;
  }
  return SESSION_TYPE_FILTER_KEYS;
}

/** Maps UI period filters to API dateFrom/dateTo (week / month / year / custom). */
export function resolveReportDateRange(
  filters: ReportFilters,
  skipDateRange = false,
): { dateFrom?: Date; dateTo?: Date } {
  if (skipDateRange) return {};

  if (filters.customDateFrom || filters.customDateTo) {
    return {
      dateFrom: filters.customDateFrom,
      dateTo: filters.customDateTo,
    };
  }

  if (filters.dateRange === 'all') return {};

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let dateFrom: Date | undefined;
  let dateTo: Date | undefined;

  switch (filters.dateRange) {
    case 'week': {
      const startOfWeek = new Date(startOfToday);
      const daysFromMonday = (startOfWeek.getDay() + 6) % 7;
      startOfWeek.setDate(startOfWeek.getDate() - daysFromMonday);
      dateFrom = startOfWeek;
      dateTo = new Date(startOfWeek);
      dateTo.setDate(dateTo.getDate() + 6);
      dateTo.setHours(23, 59, 59, 999);
      break;
    }
    case 'month':
      dateFrom = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      break;
    case 'year':
      dateFrom = new Date(now.getFullYear(), 0, 1);
      dateTo = new Date(now.getFullYear(), 11, 31);
      break;
  }

  return { dateFrom, dateTo };
}

/** Session types sent to the API: tab types intersected with the user filter. */
export function resolveReportEffectiveSessionTypes(
  activeTab: string,
  allowedTabIds: string[] | undefined,
  selectedSessionTypes: string[] | undefined,
): string[] | undefined {
  if (!activeTab || activeTab === 'all') {
    if (selectedSessionTypes && selectedSessionTypes.length > 0) return selectedSessionTypes;
    if (allowedTabIds && allowedTabIds.length > 0) {
      const typeTabs = allowedTabIds.filter(
        (id): id is keyof typeof TAB_TO_TYPES => id !== 'all' && id in TAB_TO_TYPES,
      );
      if (typeTabs.length > 0) {
        return typeTabs.flatMap((id) => TAB_TO_TYPES[id] ?? []);
      }
    }
    return undefined;
  }
  const tabTypes = TAB_TO_TYPES[activeTab as keyof typeof TAB_TO_TYPES] ?? [];
  if (selectedSessionTypes && selectedSessionTypes.length > 0) {
    const intersection = selectedSessionTypes.filter((t) => tabTypes.includes(t));
    return intersection.length > 0 ? intersection : tabTypes;
  }
  return tabTypes.length > 0 ? tabTypes : undefined;
}

/** Return the array or `undefined` when it is missing or empty. */
function undefinedIfEmpty<T>(items: T[] | undefined): T[] | undefined {
  return items && items.length > 0 ? items : undefined;
}

/** Map UI filters to list-API query params (empty arrays become `undefined`). */
export function toReportApiFilterParams(
  filters: ReportFilters,
  effectiveSessionTypes: string[] | undefined,
  options?: {
    page?: number;
    pageSize?: number;
    sessionId?: string;
    sessionGroupId?: string;
    includeSearch?: boolean;
    skipDateRange?: boolean;
  },
) {
  const sessionId = options?.sessionId?.trim() || undefined;
  const sessionGroupId = options?.sessionGroupId?.trim() || undefined;
  const skipDateRange = options?.skipDateRange ?? (!!sessionId || !!sessionGroupId);
  const { dateFrom, dateTo } = resolveReportDateRange(filters, skipDateRange);

  return {
    page: options?.page,
    pageSize: options?.pageSize,
    statuses: undefinedIfEmpty(filters.statuses),
    ratings: undefinedIfEmpty(filters.ratings),
    dateFrom,
    dateTo,
    sessionTypes: undefinedIfEmpty(effectiveSessionTypes),
    establishmentIds: undefinedIfEmpty(filters.establishmentIds),
    search: options?.includeSearch ? filters.search || undefined : undefined,
    sessionId,
    sessionGroupId,
  };
}

/** Badge color: `validated` is success; everything else is error. */
export function getReportDetailStatusColor(status: string): 'success' | 'error' {
  return status === 'validated' ? 'success' : 'error';
}

/** Author display name with first+last / stored name fallbacks. */
export function getReportDetailAuthorName(report: SessionReport): string {
  return (
    report.author?.displayName ||
    [report.author?.firstName, report.author?.lastName].filter(Boolean).join(' ') ||
    report.authorName ||
    'Auteur inconnu'
  );
}

/** Validator first+last, or null when not validated. */
export function getReportValidatorName(report: SessionReport): string | null {
  if (!report.validatedBy) return null;
  return (
    [report.validatedBy.firstName, report.validatedBy.lastName].filter(Boolean).join(' ') ||
    'Validateur inconnu'
  );
}

/** Long French weekday + date for the session, or null. */
export function formatReportSessionLongDate(report: SessionReport): string | null {
  if (!report.session?.sessionDate) return null;
  return format(new Date(report.session.sessionDate), 'EEEE d MMMM yyyy', { locale: fr });
}

/** Feedback total vs completed (rating + comment filled). */
export function getReportAuthorFeedbackCounts(report: SessionReport): {
  feedbackCount: number;
  completedCount: number;
} {
  const entries = normalizeAuthorFeedbacks(report);
  return {
    feedbackCount: entries.length,
    completedCount: entries.filter((f) => f.rating != null && f.comment?.trim()).length,
  };
}

/** Comparator using {@link REPORT_PARTICIPANT_ROLE_ORDER}. */
export function compareReportParticipantsByRole(a: { role: string }, b: { role: string }): number {
  return (
    REPORT_PARTICIPANT_ROLE_ORDER[a.role as keyof typeof REPORT_PARTICIPANT_ROLE_ORDER] -
    REPORT_PARTICIPANT_ROLE_ORDER[b.role as keyof typeof REPORT_PARTICIPANT_ROLE_ORDER]
  );
}

/** Long filled-at timestamp, or "—". */
export function formatReportFilledAtLong(filledAt: Date | string | null | undefined): string {
  if (!filledAt) return '—';
  return format(new Date(filledAt), 'd MMMM yyyy à HH:mm', { locale: fr });
}

/** French plural `s` when the report count is greater than one. */
function reportCountPluralSuffix(count: number): string {
  return count > 1 ? 's' : '';
}

/** Pluralized report count for bulk-action copy. */
export function formatReportCountLabel(count: number): string {
  const suffix = reportCountPluralSuffix(count);
  return `${count} compte${suffix} rendu${suffix}`;
}

/** Toolbar validate tooltip — empty selection vs count. */
export function getReportBulkValidateTooltip(count: number): string {
  if (count === 0) return getErrorMessage('selectSubmittedReportsToValidate');
  return `Valider ${formatReportCountLabel(count)}`;
}

/** Toolbar delete tooltip — empty selection vs count. */
export function getReportBulkDeleteTooltip(count: number): string {
  if (count === 0) return getErrorMessage('selectReportsToDelete');
  return `Supprimer ${formatReportCountLabel(count)}`;
}

/** Bulk-delete modal body (irreversible). */
export function getReportBulkDeleteDescription(count: number): string {
  return `Êtes-vous sûr de vouloir supprimer ${formatReportCountLabel(count)} ? Cette action est irréversible.`;
}

/** Bulk-validate modal body. */
export function getReportBulkValidateDescription(count: number): string {
  return `Êtes-vous sûr de vouloir valider ${formatReportCountLabel(count)} ?`;
}

/** Success toast after bulk delete. */
export function getReportBulkDeleteSuccessMessage(count: number): string {
  return `${formatReportCountLabel(count)} supprimé${reportCountPluralSuffix(count)} avec succès !`;
}

/** Success toast after bulk validate. */
export function getReportBulkValidateSuccessMessage(count: number): string {
  return `${formatReportCountLabel(count)} validé${reportCountPluralSuffix(count)} avec succès !`;
}

/** Export button tooltip — empty-filter warning unless an export is already running. */
export function getReportExportTooltip(isEmptyForExport: boolean, isExporting: boolean): string {
  if (isEmptyForExport && !isExporting) {
    return getErrorMessage('exportEmpty');
  }
  return 'Exporter la liste des rapports en Excel';
}

/** Delete-modal item name: report of the session on {date}. */
export function formatReportSessionModalItemName(report: SessionReport | null | undefined): string {
  if (!report) return 'ce rapport';
  if (!report.session?.sessionDate) return 'le rapport de la séance du date inconnue';
  const dateStr = format(new Date(report.session.sessionDate), 'd MMMM yyyy', { locale: fr });
  return `le rapport de la séance du ${dateStr}`;
}

/** Deep-compare against defaults to drive the filter badge. */
export function hasReportActiveFilters(
  filters: ReportFilters,
  defaultFilters: ReportFilters,
): boolean {
  return JSON.stringify(filters) !== JSON.stringify(defaultFilters);
}

/** Extra column when the bulk-select checkbox is visible. */
export function getReportExpandedColSpan(showBulkSelection: boolean): number {
  return showBulkSelection ? 7 : 6;
}

/** Prefer an injected list (dashboard section); else the paginated API page. */
export function resolveReportTableRows(
  externalReports: SessionReport[] | undefined,
  apiData: SessionReport[] | undefined,
): SessionReport[] {
  return externalReports ?? apiData ?? [];
}

/** Block Excel export when the filtered list is empty. */
export function isReportListEmptyForExport(
  usesExternalReports: boolean,
  dataLength: number,
  isLoading: boolean,
  totalItems: number,
): boolean {
  if (usesExternalReports) return dataLength === 0;
  return !isLoading && totalItems === 0;
}

/** Find a report by id, or null. */
export function findReportById(
  reports: SessionReport[],
  reportId: string | undefined | null,
): SessionReport | null {
  if (!reportId) return null;
  return reports.find((report) => report.id === reportId) ?? null;
}

/** Header-checkbox state: all / some selectable rows selected. */
export function getReportSelectionFlags(
  selectableIds: string[],
  selectedIds: string[],
): { allSelectableSelected: boolean; someSelectableSelected: boolean } {
  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const someSelectableSelected =
    selectableIds.some((id) => selectedIds.includes(id)) && !allSelectableSelected;
  return { allSelectableSelected, someSelectableSelected };
}

/** Toggle `value` in a list (used by filter chips). */
export function toggleListItem<T>(list: T[] | undefined, value: T): T[] {
  const current = list ?? [];
  if (current.includes(value)) return current.filter((item) => item !== value);
  return [...current, value];
}

/** Overlay caller flags onto hook RBAC (`??` so `false` from the caller wins). */
export function mergeReportPageActions(
  fromHook: ReportPageActions,
  fromProps?: { canDeleteRow?: boolean },
): ReportPageActions {
  return {
    ...fromHook,
    canDeleteRow: fromProps?.canDeleteRow ?? fromHook.canDeleteRow,
  };
}

/** Central error copy from {@link getErrorMessage}. */
export function getReportTableApiErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** True when the row `action-delete-row` permission allows delete. */
export function canDeleteSessionReport(
  report: SessionReport,
  pageActions?: Pick<ReportPageActions, 'canDeleteRow'>,
): boolean {
  return getRowPermission(report, 'action-delete-row', pageActions?.canDeleteRow ?? true);
}

/** True when the user can validate and the report is `submitted`. */
export function canValidateSessionReport(report: SessionReport, canValidate: boolean): boolean {
  return canValidate && report.status === 'submitted';
}

/** True when the row can be selected (delete or validate). */
export function isSessionReportSelectable(
  report: SessionReport,
  canValidate: boolean,
  pageActions?: Pick<ReportPageActions, 'canDeleteRow'>,
): boolean {
  return (
    canDeleteSessionReport(report, pageActions) || canValidateSessionReport(report, canValidate)
  );
}

/** GET session by id, or null when the request fails. */
async function fetchSessionOrNull(sessionId: string) {
  try {
    return await getSession(sessionId);
  } catch {
    return null;
  }
}

/** Fetch the session then pick incomplete-modal vs confirm for one report. */
export async function resolveSingleValidateTarget(
  report: SessionReport,
  sessionId: string,
  userId: string | undefined,
): Promise<SingleValidateTarget> {
  const session = await fetchSessionOrNull(sessionId);
  const validateOptions = buildReportValidationOptions(report, session, userId);
  if (shouldShowReportValidationModal(report, validateOptions)) {
    return { kind: 'incomplete', sessionId, reportId: report.id, validateOptions };
  }
  return { kind: 'confirm', sessionId, reportId: report.id };
}

/** Fetch sessions then pick incomplete-modal vs confirm for a selection. */
export async function resolveBulkValidateTarget(
  reports: SessionReport[],
  userId: string | undefined,
): Promise<BulkValidateTarget> {
  const sessionIds = [...new Set(reports.map((report) => report.sessionId))];
  const sessionEntries = await Promise.all(
    sessionIds.map(async (sessionId) => [sessionId, await fetchSessionOrNull(sessionId)] as const),
  );
  const sessionById = new Map(sessionEntries);
  const optionsByReportId: Record<string, ReportValidationOptions> = {};
  for (const report of reports) {
    optionsByReportId[report.id] = buildReportValidationOptions(
      report,
      sessionById.get(report.sessionId) ?? null,
      userId,
    );
  }
  if (shouldShowBulkReportValidationModal(reports, optionsByReportId)) {
    return { kind: 'incomplete', optionsByReportId };
  }
  return { kind: 'confirm' };
}
